import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserCircle2, ScanFace, RefreshCw } from 'lucide-react';
import { FaceDescriptor, FaceBox } from '@/utils/face';
import { ensureHumanLoaded, computeFaceDescriptorsFromBlob as computeHumanDescriptors } from '@/utils/humanEngine';

type PhotoItem = {
  id: string;
  url: string;
  originalName?: string;
  faceData?: {
    descriptors: number[][];
    boxes: FaceBox[];
  };
};

// Circle thumbnail using <img> with object-position for cropping (robust and simple)
const FaceCrop: React.FC<{ url: string; box?: FaceBox; size?: number }> = ({ url, box, size = 64 }) => {
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const w = img.naturalWidth || img.width || 0;
    const h = img.naturalHeight || img.height || 0;
    if (w && h && (w !== dims.w || h !== dims.h)) setDims({ w, h });
  };
  const onError = () => {
    // ensure we still render something centered
    if (!dims.w || !dims.h) setDims({ w: 1, h: 1 });
    // eslint-disable-next-line no-console
    console.warn('[FaceCrop] failed to load', url);
  };

  const w = dims.w || 1;
  const h = dims.h || 1;
  const cx = box ? (box.x + box.width / 2) : (w / 2);
  const cy = box ? (box.y + box.height / 2) : (h / 2);
  const px = Math.max(0, Math.min(100, (cx / w) * 100));
  const py = Math.max(0, Math.min(100, (cy / h) * 100));

  return (
    <div className="rounded-full overflow-hidden bg-gray-100 mx-auto border" style={{ width: size, height: size }}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt="face"
        className="w-full h-full object-cover"
        style={{ objectPosition: `${px}% ${py}%` }}
        onLoad={onLoad}
        onError={onError}
      />
    </div>
  );
};

interface FaceGroup {
  id: string;
  descriptor: FaceDescriptor;
  members: Array<{ photoId: string; box?: FaceBox; url: string; descriptor: number[] }>;
  thumbUrl: string | null;
  count: number;
}

interface FaceBrowserProps {
  photos: PhotoItem[];
  onFilter: (photoIds: Set<string>) => void;
  onClear: () => void;
  modelBaseUrl?: string; // default to CDN
  autoScanIntervalMs?: number;
  autoDeepScan?: boolean;
}

const DEFAULT_MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/human/models';

export const FaceBrowser: React.FC<FaceBrowserProps> = ({ photos, onFilter, onClear, modelBaseUrl = DEFAULT_MODEL_URL, autoScanIntervalMs, autoDeepScan = false }) => {
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isIndexing, setIsIndexing] = useState(false);
  const [faceGroups, setFaceGroups] = useState<FaceGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [strictMode, setStrictMode] = useState<boolean>(false);
  const [hideSingles, setHideSingles] = useState<boolean>(false);
  const [debugMode, setDebugMode] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<null | {
    totalFaces: number;
    perPhoto: Array<{ photoId: string; count: number }>;
    pairStats: { n: number; min: number; p25: number; p50: number; p75: number; max: number };
  }>(null);
  const cacheRef = useRef(new Map<string, { descriptors: number[][]; boxes: FaceBox[]; source: 'tiny' | 'deep'; url: string }>());
  const lastSigRef = useRef<string>('');

  const photosNeedingScan = useMemo(() => photos.filter(p => !p.faceData || !p.faceData.descriptors?.length), [photos]);

  const thresholds = useMemo(() => ({
    cluster: strictMode ? 0.72 : 0.88,
    merge: strictMode ? 0.72 : 0.88,
    outlier: strictMode ? 0.8 : 0.98,
  }), [strictMode]);

  const distance = (a: number[], b: number[]) => {
    let s = 0;
    const len = Math.min(a.length, b.length);
    for (let i = 0; i < len; i++) {
      const ai = a[i] ?? 0;
      const bi = b[i] ?? 0;
      const d = ai - bi;
      s += d * d;
    }
    return Math.sqrt(s);
  };

  const l2norm = (v: number[]) => {
    let sum = 0;
    for (let i = 0; i < v.length; i++) {
      const x = v[i] ?? 0;
      sum += x * x;
    }
    const r = Math.sqrt(sum) || 1;
    const out = new Array(v.length);
    for (let i = 0; i < v.length; i++) out[i] = (v[i] ?? 0) / r;
    return out;
  };

  const weightedMean = (a: number[], b: number[], wa: number, wb: number) => {
    const len = Math.min(a.length, b.length);
    const out = new Array(len);
    const denom = wa + wb || 1;
    for (let i = 0; i < len; i++) {
      const ai = a[i] ?? 0;
      const bi = b[i] ?? 0;
      out[i] = (ai * wa + bi * wb) / denom;
    }
    return out;
  };

  // Safe canvas crop via blob/objectURL to avoid CORS taint, returns data URL or original URL on failure
  const pickThumb = async (url: string, box?: FaceBox): Promise<string | null> => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) return url;
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = reject;
          el.src = objectUrl;
        });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return url;
        const sx0 = Math.floor(box ? box.x : 0);
        const sy0 = Math.floor(box ? box.y : 0);
        const sw0 = Math.floor(box ? box.width : img.width);
        const sh0 = Math.floor(box ? box.height : img.height);
        const sx = Math.max(0, Math.min(sx0, img.width - 1));
        const sy = Math.max(0, Math.min(sy0, img.height - 1));
        const sw = Math.max(1, Math.min(sw0, img.width - sx));
        const sh = Math.max(1, Math.min(sh0, img.height - sy));
        canvas.width = sw;
        canvas.height = sh;
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        URL.revokeObjectURL(objectUrl);
        return dataUrl;
      } catch {
        return url;
      }
    } catch {
      return url;
    }
  };

  const buildGroups = useCallback(async (items: Array<{ photoId: string; descriptor: number[]; url: string; box?: FaceBox }>, allowedIds: Set<string>) => {
    const groups: FaceGroup[] = [];

    for (const it of items) {
      // pick nearest centroid
      let bestIdx = -1;
      let bestDist = Number.POSITIVE_INFINITY;
      for (let idx = 0; idx < groups.length; idx++) {
        const g = groups[idx]!;
        const d = distance(g.descriptor as unknown as number[], it.descriptor);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = idx;
        }
      }

      if (bestIdx >= 0 && bestDist <= thresholds.cluster) {
        const g = groups[bestIdx]!;
        const already = g.members.some(m => m.photoId === it.photoId);
        if (!already) {
          const beforeCount = g.members.length;
          const member: { photoId: string; url: string; box?: FaceBox; descriptor: number[] } = { photoId: it.photoId, url: it.url, descriptor: it.descriptor };
          if (it.box) member.box = it.box;
          const newCentroid = weightedMean(g.descriptor as unknown as number[], it.descriptor, beforeCount, 1);
          g.descriptor = l2norm(newCentroid) as unknown as FaceDescriptor;
          g.members.push(member);
        }
      } else {
        const member: { photoId: string; url: string; box?: FaceBox; descriptor: number[] } = { photoId: it.photoId, url: it.url, descriptor: it.descriptor };
        if (it.box) member.box = it.box;
        const centroid = l2norm(it.descriptor.slice());
        groups.push({ id: `grp_${Math.random().toString(36).slice(2)}`, descriptor: centroid as unknown as FaceDescriptor, members: [member], thumbUrl: null, count: 1 });
      }
    }

    for (const g of groups) {
      g.members = g.members.filter(m => allowedIds.has(m.photoId));
    }

    const outlierThreshold = thresholds.outlier;
    for (const g of groups) {
      if (g.members.length <= 1) continue;
      g.members = g.members.filter(m => distance(g.descriptor as unknown as number[], m.descriptor) <= outlierThreshold);
    }

    for (const g of groups) {
      if (g.members.length > 0) {
        const dim = g.members[0]!.descriptor.length;
        const sum = new Array(dim).fill(0);
        for (const m of g.members) {
          for (let i = 0; i < dim; i++) sum[i] += (m.descriptor[i] ?? 0);
        }
        for (let i = 0; i < dim; i++) sum[i] = sum[i] / g.members.length;
        g.descriptor = l2norm(sum) as unknown as FaceDescriptor;
      }
    }

    const nonEmpty = groups.filter(g => g.members.length > 0);

    const mergeThreshold = thresholds.merge;
    let changed = true;
    while (changed) {
      changed = false;
      outer: for (let i = 0; i < nonEmpty.length; i++) {
        for (let j = i + 1; j < nonEmpty.length; j++) {
          const gi = nonEmpty[i]!;
          const gj = nonEmpty[j]!;
          const d = distance(gi.descriptor as unknown as number[], gj.descriptor as unknown as number[]);
          if (d <= mergeThreshold) {
            const seen = new Set(gi.members.map(m => m.photoId));
            for (const m of gj.members) {
              if (!seen.has(m.photoId)) gi.members.push(m);
            }
            if (gi.members.length > 0) {
              const dim = gi.members[0]!.descriptor.length;
              const sum = new Array(dim).fill(0);
              for (const m of gi.members) {
                for (let k = 0; k < dim; k++) sum[k] += (m.descriptor[k] ?? 0);
              }
              for (let k = 0; k < dim; k++) sum[k] = sum[k] / gi.members.length;
              gi.descriptor = l2norm(sum) as unknown as FaceDescriptor;
            }
            nonEmpty.splice(j, 1);
            changed = true;
            break outer;
          }
        }
      }
    }

    for (const g of nonEmpty) {
      const rep = g.members[0];
      g.thumbUrl = rep ? await pickThumb(rep.url, rep.box) : null;
    }

    for (const g of nonEmpty) {
      const unique = new Set(g.members.map(m => m.photoId));
      g.count = unique.size;
    }

    const finalGroups = hideSingles ? nonEmpty.filter(g => g.count >= 2) : nonEmpty;
    finalGroups.sort((a, b) => b.count - a.count);
    setFaceGroups(finalGroups);
  }, [hideSingles, thresholds]);

  const indexFaces = useCallback(async (deep = false) => {
    setError(null);
    try {
      setIsLoadingModels(true);
      await ensureHumanLoaded(modelBaseUrl);
    } catch (e: any) {
      setError('Failed to load face models');
      setIsLoadingModels(false);
      return;
    }

    try {
      setIsIndexing(true);
      const collected: Array<{ photoId: string; descriptor: number[]; url: string; box?: FaceBox }> = [];

      for (const p of photos) {
        const cached = cacheRef.current.get(p.id);
        if (deep) {
          if (cached && cached.source === 'deep' && cached.url === p.url) {
            cached.descriptors.forEach((d, idx) => {
              const item: { photoId: string; descriptor: number[]; url: string; box?: FaceBox } = { photoId: p.id, descriptor: d, url: p.url };
              const bx = cached.boxes?.[idx];
              if (bx) item.box = bx;
              collected.push(item);
            });
            continue;
          }
        } else {
          if (cached && cached.url === p.url) {
            cached.descriptors.forEach((d, idx) => {
              const item: { photoId: string; descriptor: number[]; url: string; box?: FaceBox } = { photoId: p.id, descriptor: d, url: p.url };
              const bx = cached.boxes?.[idx];
              if (bx) item.box = bx;
              collected.push(item);
            });
            continue;
          }
          if (p.faceData && p.faceData.descriptors?.length) {
            p.faceData.descriptors.forEach((d, idx) => {
              const item: { photoId: string; descriptor: number[]; url: string; box?: FaceBox } = { photoId: p.id, descriptor: d, url: p.url };
              const bx = p.faceData?.boxes?.[idx];
              if (bx) item.box = bx;
              collected.push(item);
            });
            cacheRef.current.set(p.id, { descriptors: p.faceData.descriptors, boxes: p.faceData.boxes || [], source: 'tiny', url: p.url });
            continue;
          }
        }

        // For Human engine, deep vs normal uses same call; Strict toggles tighter thresholds
        try {
          const resp = await fetch(p.url, { mode: 'cors' });
          const blob = await resp.blob();
          const { descriptors, boxes } = await computeHumanDescriptors(blob, { strict: strictMode });
          const keepIdx: number[] = [];
          const iou = (a?: FaceBox, b?: FaceBox) => {
            if (!a || !b) return 0;
            const ax1 = a.x, ay1 = a.y, ax2 = a.x + a.width, ay2 = a.y + a.height;
            const bx1 = b.x, by1 = b.y, bx2 = b.x + b.width, by2 = b.y + b.height;
            const ix1 = Math.max(ax1, bx1), iy1 = Math.max(ay1, by1), ix2 = Math.min(ax2, bx2), iy2 = Math.min(ay2, by2);
            const iw = Math.max(0, ix2 - ix1), ih = Math.max(0, iy2 - iy1);
            const inter = iw * ih;
            const union = a.width * a.height + b.width * b.height - inter || 1;
            return inter / union;
          };
          for (let i = 0; i < descriptors.length; i++) {
            const di = descriptors[i]!;
            const bi = boxes?.[i];
            let dup = false;
            for (const k of keepIdx) {
              const dj = descriptors[k]!;
              const bk = boxes?.[k];
              if (distance(di, dj) < 0.5 || iou(bi, bk) >= 0.6) { dup = true; break; }
            }
            if (!dup) keepIdx.push(i);
          }
          const usedD: number[][] = [];
          const usedB: FaceBox[] = [];
          keepIdx.forEach((idx) => {
            const d = descriptors[idx]!;
            const item: { photoId: string; descriptor: number[]; url: string; box?: FaceBox } = { photoId: p.id, descriptor: d, url: p.url };
            const bx = boxes?.[idx];
            if (bx) item.box = bx;
            collected.push(item);
            usedD.push(d);
            if (bx) usedB.push(bx);
          });
          cacheRef.current.set(p.id, { descriptors: usedD, boxes: usedB, source: deep ? 'deep' : 'tiny', url: p.url });
        } catch {
        }
      }

      if (debugMode) {
        // Build per-photo counts
        const map = new Map<string, number>();
        for (const it of collected) map.set(it.photoId, (map.get(it.photoId) || 0) + 1);
        const perPhoto = Array.from(map.entries()).map(([photoId, count]) => ({ photoId, count })).sort((a, b) => a.photoId.localeCompare(b.photoId));
        // Pairwise distances across different photos
        const dists: number[] = [];
        for (let i = 0; i < collected.length; i++) {
          for (let j = i + 1; j < collected.length; j++) {
            if (collected[i]!.photoId === collected[j]!.photoId) continue;
            dists.push(distance(collected[i]!.descriptor, collected[j]!.descriptor));
          }
        }
        dists.sort((a, b) => a - b);
        const pick = (p: number): number => {
          if (!dists.length) return 0;
          const idx = Math.min(dists.length - 1, Math.floor(p * (dists.length - 1)));
          const val = dists[idx];
          return typeof val === 'number' ? val : 0;
        };
        const last = dists.length ? dists[dists.length - 1]! : 0;
        const pairStats: { n: number; min: number; p25: number; p50: number; p75: number; max: number } = {
          n: dists.length,
          min: dists.length ? dists[0]! : 0,
          p25: pick(0.25),
          p50: pick(0.5),
          p75: pick(0.75),
          max: last,
        };
        setDebugInfo({ totalFaces: collected.length, perPhoto, pairStats });
        // Console detail for deeper inspection
        // eslint-disable-next-line no-console
        console.log('[FaceBrowser][debug] faces:', collected.length, 'perPhoto:', perPhoto, 'pairStats:', pairStats);
      }

      const allowed = new Set(photos.map(p => p.id));
      await buildGroups(collected, allowed);
    } finally {
      setIsLoadingModels(false);
      setIsIndexing(false);
    }
  }, [photos, modelBaseUrl, buildGroups, debugMode]);

  const handleSelectGroup = (g: FaceGroup) => {
    const ids = new Set<string>(g.members.map(m => m.photoId));
    onFilter(ids);
  };

  const hasAny = faceGroups.length > 0;

  useEffect(() => {
    if (isIndexing || isLoadingModels) return;
    indexFaces(autoDeepScan);
  }, []);

  // Re-index when the photo list changes so counts stay in sync
  useEffect(() => {
    if (isIndexing || isLoadingModels) return;
    if (!photos || photos.length === 0) { setFaceGroups([]); lastSigRef.current = ''; return; }
    const sig = JSON.stringify(photos.map(p => p.id).sort());
    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;
    indexFaces(false);
  }, [photos, indexFaces, isIndexing, isLoadingModels]);

  useEffect(() => {
    if (!autoScanIntervalMs) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (isIndexing || isLoadingModels) return;
      indexFaces(autoDeepScan);
    }, autoScanIntervalMs);
    return () => window.clearInterval(id);
  }, [autoScanIntervalMs, autoDeepScan, indexFaces, isIndexing, isLoadingModels]);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sage-700">
          <ScanFace className="w-5 h-5" />
          <span className="font-medium">Find my photos</span>
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1 text-xs text-gray-600 select-none">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={strictMode}
              onChange={(e) => {
                setStrictMode(e.target.checked);
              }}
            />
            Strict
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-600 select-none">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={hideSingles}
              onChange={(e) => {
                setHideSingles(e.target.checked);
              }}
            />
            Hide singles
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-600 select-none">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={debugMode}
              onChange={(e) => setDebugMode(e.target.checked)}
            />
            Debug
          </label>
          <button
            onClick={() => indexFaces(false)}
            disabled={isLoadingModels || isIndexing}
            className="px-3 py-2 text-sm bg-sage-600 text-white rounded-md disabled:opacity-50"
          >
            {isIndexing || isLoadingModels ? 'Scanning…' : 'Scan Faces'}
          </button>
          <button
            onClick={() => indexFaces(true)}
            disabled={isLoadingModels || isIndexing}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md disabled:opacity-50"
          >
            Deep Scan
          </button>
          <button onClick={onClear} className="px-3 py-2 text-sm bg-gray-100 rounded-md">Clear</button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 mb-3">{error}</div>
      )}

      {!hasAny && !isIndexing && (
        <div className="text-sm text-gray-600">{photosNeedingScan.length > 0 ? 'Click Scan Faces to build a face list.' : 'No faces found yet.'}</div>
      )}

      {hasAny && (
        <div className="flex gap-3 overflow-x-auto py-2">
          {faceGroups.map((g) => {
            const rep = g.members[0];
            return (
              <button key={g.id} onClick={() => handleSelectGroup(g)} className="flex-shrink-0 w-20" title={`${g.count} photos`}>
                {rep ? (
                  g.thumbUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={g.thumbUrl} alt="face" className="w-16 h-16 rounded-full object-cover border mx-auto" />
                  ) : (
                    <FaceCrop url={rep.url} {...(rep.box ? { box: rep.box } : {})} />
                  )
                ) : (
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 mx-auto border flex items-center justify-center text-gray-400">
                    <UserCircle2 className="w-8 h-8" />
                  </div>
                )}
                <div className="text-xs text-center mt-1 text-gray-600">{g.count} photo{g.count > 1 ? 's' : ''}</div>
              </button>
            );
          })}
        </div>
      )}

      {debugMode && debugInfo && (
        <div className="mt-3 text-xs text-gray-600 space-y-1">
          <div>Total faces: {debugInfo.totalFaces}</div>
          <div>Pairwise distances (different photos): min {debugInfo.pairStats.min.toFixed(3)} • p25 {debugInfo.pairStats.p25.toFixed(3)} • p50 {debugInfo.pairStats.p50.toFixed(3)} • p75 {debugInfo.pairStats.p75.toFixed(3)} • max {debugInfo.pairStats.max.toFixed(3)} • n={debugInfo.pairStats.n}</div>
          <div className="flex flex-wrap gap-2">
            {debugInfo.perPhoto.map((p) => (
              <span key={p.photoId} className="px-2 py-1 bg-gray-100 rounded">{p.photoId}: {p.count}</span>
            ))}
          </div>
        </div>
      )}

      {(isLoadingModels || isIndexing) && (
        <div className="flex items-center gap-2 text-sm text-gray-600"><RefreshCw className="w-4 h-4 animate-spin" />Processing…</div>
      )}
    </div>
  );
};
